import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ArrowUpRight } from "lucide-react";

// V2.1 Challenge tile images — pixelweise verifiziert.
// Zeigt für jede Herausforderung das passende Modul-Rendering.
const IMGS = [
  // 01 Klimaanpassung → Baummodul-Reihe mit Lavendel (grüne Infrastruktur, abends)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/22qru26t_grafik.png",
  // 02 Hitze → Grünes Baumdach / Pergola (Schatten + Kühlung)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/dic8bwnw_grafik.png",
  // 03 Starkregen → Bepflanzte Modul-Reihe (Substrat als Retentionsraum)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/so4xbqiz_grafik.png",
  // 04 Stadtgrün → Lounge Cube (vertikaler Garten am Museum)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/49iizw9u_grafik.png",
  // 05 Aufenthaltsqualität → Lounge Cube (Sonnenuntergang mit Lavendel)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/1a4t87rn_grafik.png",
  // 06 Mobilität → Mobility Cube (E-Bike Ladestation)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/78890o30_grafik.png",
  // 07 Smart City → Smart Cube (Berlin Wayfinding & Fernsehturm)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/xfdgznb7_grafik.png",
  // 08 Sicherheit → Massive Schutzmodule mit Lichtnut (robuste Linie)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/mijzsfl2_grafik.png",
  // 09 Finanzierung → Media-Stele bei Nacht (Werbeflächen als Finanzierungsbaustein)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/pn5a8q3f_grafik.png",
];

export default function Challenges() {
  const { t } = useLang();
  const c = t.challenges;
  return (
    <section
      id="challenges"
      data-testid="section-challenges"
      className="relative bg-[#1F2328] py-32 md:py-44"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8">
                <span className="h-px w-8 bg-[#F5D77A]" />
                {c.kicker}
              </div>
              <h2
                className="font-display font-light tracking-[-0.03em] leading-[1.02] text-white"
                style={{ fontSize: "clamp(2.5rem, 6.5vw, 6rem)" }}
              >
                {c.title}
              </h2>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="text-white/55 text-base leading-relaxed"
            >
              {c.sub}
            </motion.p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {c.items.map((it, i) => (
            <motion.a
              key={it.t}
              href="#cta"
              data-testid={`challenge-tile-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="group relative block overflow-hidden border border-white/8 hover:border-[#F5D77A]/50 bg-[#16191D] transition-colors"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={IMGS[i]}
                  alt={it.t}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328] via-[#1F2328]/40 to-transparent" />
                <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                  <span className="text-[10px] tracking-[0.35em] uppercase text-white/70 font-display">
                    Challenge · {it.n}
                  </span>
                  <ArrowUpRight
                    className="h-5 w-5 text-white/50 group-hover:text-[#F5D77A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="absolute bottom-7 left-7 right-7">
                  <h3
                    className="font-display font-light tracking-[-0.02em] leading-[1] text-white"
                    style={{ fontSize: "clamp(1.6rem, 2.2vw, 2.2rem)" }}
                  >
                    {it.t}
                  </h3>
                  <p className="mt-3 max-w-md text-[14px] text-white/60 leading-relaxed">
                    {it.d}
                  </p>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
